<template>
  <b-container>
    <TheTitle>商戶</TheTitle>
    <OverviewList :overviews="overviews"
                  class-name="col-6 col-md-3"
                  v-stream:select="onOverviewSelect$"
                  :loading="loading"
    ></OverviewList>
    <TheListingTitle
      title="商戶列表"
      :selectedKeywordAndFilters="selectedKeywordAndFilters"
      @reset-click="initialize"
    ></TheListingTitle>
    <section class="table-content">
      <BaseFilterToolbar @reset="initialize" :loading="loading">
        <div class="d-flex">
          <label>
            <b-form-select class="filter keyword-select" v-model="filter.keywordType">
              <b-form-select-option
                  v-for="option in filteredKeywordSelectOptions"
                  :value="option.value"
                  :key="option.text">
                  {{ option.text }}
                </b-form-select-option>
            </b-form-select>
          </label>
          <label id="keyword-filter">
            <input
              v-model="keyword"
              type="text"
              class="form-control"
              placeholder="關鍵字..."
              aria-label="關鍵字..."
              aria-describedby="button-addon1"
            />
          </label>
        </div>
          <label>
            <b-form-select class="filter" v-model="filter.tagId">
              <b-form-select-option :value="undefined">分類</b-form-select-option>
              <b-form-select-option v-for="tag in tags" :key="tag.id" :value="tag.id">
                {{ (tag.hasOwnProperty('parentId') ? '' : '---') + tag.name }}
              </b-form-select-option>
            </b-form-select>
          </label>
          <label>
            <b-form-select class="filter" v-model="filter.class">
              <b-form-select-option
                  v-for="option in userClassSelectOptions"
                  :value="option.value"
                  :key="option.text">
                  {{ option.text }}
                </b-form-select-option>
            </b-form-select>
          </label>
          <label>
            <b-form-select class="filter" v-model="filter.status">
              <b-form-select-option
                v-for="option in filteredStatusSelectOptions"
                :value="option.value"
                :key="option.text"
              >
                {{ option.text }}
              </b-form-select-option>
            </b-form-select>
          </label>
          <label>
            <b-form-select class="filter" v-model="filter.isShown">
              <b-form-select-option
                v-for="option in filteredIsShownSelectOptions"
                :value="option.value"
                :key="option.text"
              >
                {{ option.text }}
              </b-form-select-option>
            </b-form-select>
          </label>
          <template v-slot:end>
            <BaseButton v-stream:click="exportData$" class="app--mobile-hidden">匯出</BaseButton>
          </template>
      </BaseFilterToolbar>
      <DataTable
        v-show="list"
        :fields="fields"
        :mobileFields="mobileFields"
        :items="list"
        :total-result="total"
        :options.sync="options"
        :loading="loading"
        selectable
        @row-clicked="goToItem"
        @row-selected="onRowSelected"
        v-stream:export-click="exportData$"
        mobile-searchable
        mobile-exportable
        showRowDetails
      >
        <template v-slot:cell(status)="{ value }">
          <StatusLabel :item="value"></StatusLabel>
        </template>
        <template v-slot:cell(contactEmail)="{ value }">
          <div class="contact-email">{{ value }}</div>
        </template>

        <!-- mobile only -->
        <template v-slot:isShown="{ item }">
          <div class="contact-email">{{ item.isShown == "顯示" ? "顯示" : "不顯示" }}</div>
        </template>
        <!-- end of mobile only -->
      </DataTable>
      <b-skeleton-table v-if="!list" :rows="15" :columns="5"></b-skeleton-table>
    </section>
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex';
import { scheduled, asyncScheduler } from 'rxjs';
import axios from 'axios';
import {
  tap, switchMap, debounceTime, map, pluck, mergeAll,
} from 'rxjs/operators';
import {
  BFormSelect, BFormSelectOption, BContainer, BSkeletonTable,
} from 'bootstrap-vue';
import http from '@/helpers/http';
import BaseButton from '@/components/BaseButton.vue';
import dataTableMixin from '../../mixins/data-table';
import exportMixin from '../../mixins/export';
import filtersQueryStringMixin from '../../mixins/filtersQueryString';
import optionsQueryStringMixin from '../../mixins/optionsQueryString';
import getCouponStatus from '../../helpers/coupon-status';
import getUserClass from '../../helpers/user-class';
import DataTable from '../../components/DataTable.vue';
import OverviewList from '../../components/overview/OverviewList.vue';
import StatusLabel from '../../components/StatusLabel.vue';
import TheTitle from '../../components/TheTitle.vue';
import TheListingTitle from '../../components/TheListingTitle.vue';
import BaseFilterToolbar from '../../components/BaseFilterToolbar.vue';

export default {
  mixins: [dataTableMixin, exportMixin, filtersQueryStringMixin, optionsQueryStringMixin],
  components: {
    StatusLabel,
    DataTable,
    TheTitle,
    TheListingTitle,
    BFormSelect,
    BFormSelectOption,
    BContainer,
    BSkeletonTable,
    OverviewList,
    BaseFilterToolbar,
    BaseButton,
  },
  data: () => ({
    fields: [
      { key: 'id', label: '商戶ID', sortable: true },
      { key: 'userid', label: '用戶ID', sortable: true },
      { key: 'username', label: '用戶名稱', sortable: true },
      { key: 'brandName', label: '品牌名稱', sortable: true },
      { key: 'name', label: '商戶名稱', sortable: true },
      { key: 'companyName', label: '公司名稱', sortable: true },
      {
        key: 'class',
        label: '帳戶級別',
        sortable: true,
        formatter: (value, key, item) => getUserClass(item.class).text,
      },
      {
        key: 'status',
        label: '狀態',
        sortable: true,
        formatter: (value, key, item) => getCouponStatus(item.status),
      },
      { key: 'brc', label: '商業登記號碼', sortable: true },
      { key: 'contactPhone', label: '申請人聯絡電話', sortable: true },
      { key: 'contactEmail', label: '申請人聯絡電郵', sortable: true },
      {
        key: 'isShown',
        label: '是否顯示',
        sortable: true,
        formatter: (value) => {
          switch (value) {
            case 0:
              return '不顯示';
            case 1:
              return '顯示';
            default:
              return '不顯示';
          }
        },
      },
      { key: 'order', label: '次序', sortable: true },
      { key: 'createdAt', label: '創建時間', sortable: true },
      { key: 'lastLoginTime', label: '最後登入時間', sortable: true },
    ],
    mobileFields: [
      { key: 'name', label: '商戶名稱', sortable: true },
      {
        key: 'status',
        label: '狀態',
        formatter: (value, key, item) => getCouponStatus(item.status),
        thStyle: {
          display: 'none',
        },
      },
    ],
    filter: {
      status: undefined,
      tagId: undefined,
      class: undefined,
      isShown: undefined,
      keywordType: 'all',
    },
    options: {
      page: 1,
      size: 15,
      order: 'desc',
    },
    keyword: '',
    keywordSelectOptions: [
      { value: 'all', text: '所有關鍵字' },
      { value: 'id', text: '用戶編號' },
      { value: 'brc', text: '商業登記號碼' },
      { value: 'contactPhone', text: '申請人聯絡電話' },
      { value: 'contactEmail', text: '申請人聯絡電郵' },
      { value: 'name', text: '商戶名稱' },
      { value: 'username', text: '用戶名稱' },
      { value: 'companyName', text: '公司名稱' },
      { value: 'brandName', text: '品牌名稱' },
    ],
    statusSelectOptions: [
      { value: undefined, text: '所有狀態' },
      { value: 'PENDING', text: '審批中' },
      { value: 'ACTIVATED', text: '已啓用' },
      { value: 'INACTIVATED', text: '已停用' },
      { value: 'REJECTED', text: '已拒絕' },
    ],
    userClassSelectOptions: [
      { value: undefined, text: '所有級別' },
      { value: 'NORMAL', text: '普通商戶' },
      { value: 'PREMIUM', text: '星級商戶' },
    ],
    isShownSelectOptions: [
      { value: undefined, text: '是否顯示' },
      { value: '1', text: '顯示' },
      { value: '0', text: '不顯示' },
    ],
    selected: [],
  }),
  computed: {
    ...mapGetters(['role']),
    overviews() {
      return this.result?.overviews;
    },
    list() {
      return this.result?.list.result;
    },
  },
  domStreams: ['onOverviewSelect$', 'exportData$'],
  subscriptions() {
    const getData$ = () => {
      const data = {
        ...this.filter,
        q: this.keyword,
      };
      // only admin can access merchant list
      return this.httpRequest$(
        {
          method: 'GET',
          url: `/agc-${this.role}-api/merchants`,
        },
        data,
      );
    };

    return {
      tagTree: http({
        method: 'GET',
        url: '/agc-public-api/merchanttags',
      })
        .pipe(
          map(({ result }) => result),
        ),
      // flatten the tagTree for selecting subtag(children)
      tags: this.$watchAsObservable('tagTree').pipe(
        pluck('newValue'),
        map((tagObjects) => {
          const result = [];
          tagObjects.forEach((tagObject) => {
            if (tagObject.children) {
              const el = { ...tagObject, ...{} };
              delete el.children;
              result.push(el);
              Object.values(tagObject.children).map((children) => result.push(children));
            } else {
              result.push(tagObject);
            }
          });
          return result;
        }),
      ),
      exportData: this.exportData$.pipe(
        switchMap(() => axios({
          method: 'GET',
          url: `/agc-${this.role}-api/export/merchantList`,
          params: {
            ...this.filter,
            q: this.keyword,
            selectedItem: this.selected.map((item) => item.id).join(','),
          },
          responseType: 'blob',
        }).then((res) => {
          this.downloadFile(res.data, this.generateFileName('merchant-list-data'));
        })),
      ),
      result: scheduled(
        this.scheduleTasks, asyncScheduler,
      ).pipe(
        mergeAll(),
        tap(() => {
          this.loading = true;
        }),
        debounceTime(100),
        switchMap(getData$),
      ),
      onOverviewSelect: this.onOverviewSelect$.pipe(
        pluck('event', 'msg'),
        tap((status) => {
          this.filter.status = this.filter.status === status ? undefined : status;
        }),
      ),
    };
  },
  methods: {
    goToItem({ id }) {
      this.$router.push({
        path: `/${this.role}/merchants/${id}`,
      });
    },
    onRowSelected(items) {
      this.selected = items;
    },
    initialize() {
      this.filter.keywordType = 'all';
      this.keyword = '';
      this.filter.status = undefined;
      this.filter.tagId = undefined;
      this.filter.class = undefined;
      this.filter.isShown = undefined;
      setTimeout(() => {
        this.loading = false;
      }, 550);
      // when user updates filter too fast, the loading state will not set back to false
      // this time must be bigger than debounceTime's time
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../assets/scss/data-table/list-table-content';

.contact-email {
  max-width: 12rem;
}
</style>
